import { AlertCircle, ImageOff } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

type UploadStatus = "PENDING" | "UPLOADING" | "COMPLETED" | "FAILED";

interface OfflineImageProps {
  src: string;
  alt: string;
  className?: string;
  uploadStatus?: UploadStatus;
}

/**
 * Image component with offline fallback and upload status indicators.
 * Shows different placeholders based on upload status or network availability.
 */
export function OfflineImage({
  src,
  alt,
  className,
  uploadStatus,
}: OfflineImageProps) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Reset state when src changes
  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [src]);

  // Show uploading state
  if (uploadStatus === "UPLOADING" || uploadStatus === "PENDING") {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center bg-muted text-muted-foreground",
          className,
        )}
      >
        <span className="text-[10px] text-center px-1">Uploading...</span>
      </div>
    );
  }

  // Show failed upload state
  if (uploadStatus === "FAILED") {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center bg-destructive/10 text-destructive",
          className,
        )}
      >
        <AlertCircle className="h-6 w-6 mb-1" />
        <span className="text-[10px] text-center px-1">Upload failed</span>
      </div>
    );
  }

  // Show offline/error state
  if (hasError || !src) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center bg-muted text-muted-foreground",
          className,
        )}
      >
        <ImageOff className="h-6 w-6 mb-1" />
        <span className="text-[10px] text-center px-1">
          Unavailable offline
        </span>
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div
          className={cn(
            "flex items-center justify-center bg-muted animate-pulse",
            className,
          )}
        />
      )}
      <img
        src={src}
        alt={alt}
        className={cn(className, isLoading && "hidden")}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
      />
    </>
  );
}
